import { useEffect, useRef, useState, useCallback } from "react";

export interface UseApiResource<T> {
  data?: T;
  loading: boolean;
  error?: string;
  reload: (opts?: { silent?: boolean }) => Promise<void>;
  setData: (next: T | undefined) => void;
  apply: (mutator: (current: T | undefined) => T | undefined) => void;
}

export function useResource<T>(loader: () => Promise<T>, deps: unknown[] = []): UseApiResource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const alive = useRef(true);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const depsKey = useDepsKey(deps);

  const reload = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(undefined);
    try {
      const result = await loaderRef.current();
      if (alive.current) setData(result);
    } catch (err: any) {
      if (alive.current) setError(err?.message ?? "Failed to load");
    } finally {
      if (alive.current && !opts?.silent) setLoading(false);
    }
  }, [depsKey]);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  const apply = useCallback((mutator: (current: T | undefined) => T | undefined) => {
    setData((current) => mutator(current));
  }, []);

  return { data, loading, error, reload, setData, apply };
}

function useDepsKey(deps: unknown[]): string {
  const ref = useRef<string>("__initial__");
  const next = stableKey(deps);
  if (ref.current !== next) {
    ref.current = next;
  }
  return ref.current;
}

function stableKey(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(Math.random());
  }
}
